// Passkey (WebAuthn) client — thin wrapper over @simplewebauthn/browser that
// drives the four server routes and handles the ArrayBuffer <-> base64url
// plumbing so components stay clean. TOTP stays the fallback; a passkey login
// with enforced user-verification issues a full session directly.
import {
  startRegistration,
  startAuthentication,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import { apiGet, apiPost } from './api';
import type { Session } from './types';

export interface PasskeySummary {
  id: string;
  label: string;
  deviceType: string;
  backedUp: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

// A platform-authenticator/WebAuthn-capable browser.
export const passkeysSupported = (): boolean =>
  typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined';

// Register a new passkey for the signed-in, 2FA-enrolled user.
export async function registerPasskey(csrfToken: string, label?: string): Promise<PasskeySummary[]> {
  const { options, challengeToken } = await apiPost<{
    options: PublicKeyCredentialCreationOptionsJSON;
    challengeToken: string;
  }>('/api/account/passkey/register-options', { csrfToken });
  const response = await startRegistration({ optionsJSON: options });
  const { passkeys } = await apiPost<{ passkeys: PasskeySummary[] }>('/api/account/passkey/register-verify', {
    csrfToken,
    challengeToken,
    response,
    label,
  });
  return passkeys;
}

// Sign in with a passkey (email-first). Returns the new session for the caller
// to install via setSession.
// 2026-10-19: once 7 days have passed since the user last gave an authenticator
// code, the server answers a verified passkey with a code challenge instead of a
// session (`reverify`), and the caller shows the code step once.
export type PasskeyResult = { session: Session } | { twoFactorRequired: true; challenge: string; reverify?: string };
export async function authenticateWithPasskey(email: string): Promise<PasskeyResult> {
  const { options, challengeToken } = await apiPost<{
    options: PublicKeyCredentialRequestOptionsJSON;
    challengeToken: string;
  }>('/api/auth/passkey/login-options', { email });
  const response = await startAuthentication({ optionsJSON: options });
  const res = await apiPost<{ session?: Session; twoFactorRequired?: boolean; challenge?: string; reverify?: string }>('/api/auth/passkey/login-verify', {
    challengeToken,
    response,
  });
  if (res.twoFactorRequired && res.challenge) return { twoFactorRequired: true, challenge: res.challenge, reverify: res.reverify };
  if (!res.session) throw new Error('Could not sign in with a passkey.');
  return { session: res.session };
}

export const listPasskeys = () => apiGet<{ passkeys: PasskeySummary[] }>('/api/account/passkey/list');

export const removePasskey = (csrfToken: string, id: string) =>
  apiPost<{ passkeys: PasskeySummary[] }>('/api/account/passkey/remove', { csrfToken, id });
